const ATTRIBUTION_KEY = 'fairday_first_touch_attribution_v1';
const LAST_TOUCH_KEY = 'fairday_last_touch_attribution_v1';
const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export interface AcquisitionAttribution {
  acquisition_source?: string;
  acquisition_medium?: string;
  acquisition_campaign?: string;
  acquisition_landing_path: string;
  acquisition_captured_at: string;
}

type AnalyticsAttribution = AcquisitionAttribution & {
  last_touch_source?: string;
  last_touch_medium?: string;
  last_touch_campaign?: string;
  last_touch_landing_path?: string;
  last_touch_captured_at?: string;
};

function canonicalChannel(value: string | null, allowed: Set<string>): string | undefined {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) return undefined;
  return allowed.has(normalized) ? normalized : 'other';
}

function opaqueCampaignId(value: string | null): string | undefined {
  if (!value) return undefined;
  let hash = 2166136261;
  for (const char of value.trim().slice(0, 200)) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return `campaign_${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export function parseAcquisitionAttribution(
  search: string,
  pathname: string,
  now = new Date(),
): AcquisitionAttribution | null {
  const params = new URLSearchParams(search);
  const source = canonicalChannel(params.get('utm_source'), new Set([
    'google', 'bing', 'facebook', 'instagram', 'tiktok', 'reddit', 'youtube',
    'email', 'newsletter', 'affiliate', 'partner', 'direct',
  ]));
  const medium = canonicalChannel(params.get('utm_medium'), new Set([
    'cpc', 'paid_social', 'organic', 'social', 'email', 'referral', 'affiliate', 'display',
  ]));
  const campaign = opaqueCampaignId(params.get('utm_campaign'));
  if (!source && !medium && !campaign) return null;
  return {
    ...(source ? { acquisition_source: source } : {}),
    ...(medium ? { acquisition_medium: medium } : {}),
    ...(campaign ? { acquisition_campaign: campaign } : {}),
    acquisition_landing_path: pathname.startsWith('/') ? pathname.slice(0, 200) : '/',
    acquisition_captured_at: now.toISOString(),
  };
}

export function captureFirstTouchAttribution(): void {
  if (typeof window === 'undefined') return;
  try {
    const attribution = parseAcquisitionAttribution(window.location.search, window.location.pathname);
    if (!attribution) return;
    if (!readStoredAttribution(ATTRIBUTION_KEY)) {
      window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
    }
    window.localStorage.setItem(LAST_TOUCH_KEY, JSON.stringify(attribution));
  } catch {
    // Storage can be unavailable in strict privacy modes; the funnel still works.
  }
}

function readStoredAttribution(key: string, now = new Date()): AcquisitionAttribution | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AcquisitionAttribution;
    const capturedAt = new Date(parsed.acquisition_captured_at).getTime();
    if (!Number.isFinite(capturedAt) || now.getTime() - capturedAt > ATTRIBUTION_TTL_MS) {
      window.localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function getAcquisitionAttribution(now = new Date()): AnalyticsAttribution | null {
  const first = readStoredAttribution(ATTRIBUTION_KEY, now);
  const last = readStoredAttribution(LAST_TOUCH_KEY, now);
  if (!first && !last) return null;
  const canonicalFirst = first || last!;
  return {
    ...canonicalFirst,
    ...(last?.acquisition_source ? { last_touch_source: last.acquisition_source } : {}),
    ...(last?.acquisition_medium ? { last_touch_medium: last.acquisition_medium } : {}),
    ...(last?.acquisition_campaign ? { last_touch_campaign: last.acquisition_campaign } : {}),
    ...(last ? {
      last_touch_landing_path: last.acquisition_landing_path,
      last_touch_captured_at: last.acquisition_captured_at,
    } : {}),
  };
}
